import moment from "moment";
import { mapHotelUsuarioList } from "./mapHotelUsuario";

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export const getWeekStart = (date) =>
    moment(date).startOf("isoWeek");

export const getWeekDays = (weekStart) =>
    Array.from({ length: 7 }, (_, i) =>
        moment(weekStart).add(i, "days")
    );

export const formatWeekRangeLabel = (weekStart) => {
    const start = moment(weekStart);
    const end = moment(weekStart).add(6, "days");
    if (start.month() === end.month()) {
        return `${start.format("D")} – ${end.format("D MMM YYYY")}`;
    }
    return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
};

export const formatDayHeader = (day) => {
    const m = moment(day);
    const label = DAY_LABELS[m.isoWeekday() - 1];
    return `${label}\n${m.format("DD/MM")}`;
};

const STATUS_ORDER = ["R", "I", "O", "C"];

export const toDateKey = (value) => {
    if (value == null || value === "") return null;
    const parsed = moment(value, [
        moment.ISO_8601,
        "YYYY-MM-DD",
        "YYYY-MM-DD HH:mm:ss",
        "DD/MM/YYYY",
        "DD/MM/YYYY HH:mm",
        "DD/MM/YYYY HH:mm:ss",
    ]);

    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");

    const str = String(value).trim();
    if (str.length >= 10) {
        const cleanStr = str.substring(0, 10);
        if (cleanStr.includes('/')) {
            const [d, m, a] = cleanStr.split('/');
            return `${a}-${m}-${d}`; // Lo normaliza a YYYY-MM-DD
        }
        return cleanStr;
    }
    
    return null;
};

/**
 * Indica si el huésped ocupa plaza en la fecha dada.
 * No cuenta cancelados ni quienes salen ese día (fechaHasta).
 */


// export const occupiesPlazaOnDate = (guest, dateKey) => {
//     const code = String(guest.reservaStatusCode || "").trim().toUpperCase();
//     if (code === "C") return false;

//     const desde = toDateKey(guest.fechaDesde);
//     const hasta = toDateKey(guest.fechaHasta);

//     const TODAY = moment().format("YYYY-MM-DD");

//      if (code === "O" && hasta < TODAY) {
//         return dateKey === desde;
//     }

//     if (dateKey < TODAY) {
//         const estuvoHospedado = code === "I" || code === "O";

//         const esNocheDeEstadia = dateKey >= desde && dateKey < hasta;

//         return estuvoHospedado && esNocheDeEstadia;
//     }

//     // CELDA DE HOY 
//     if (dateKey === TODAY) {
//         if (code === "O") return false; 
//         if (code === "I" && dateKey < hasta) return true; 

     
//         if (code === "I" && dateKey === hasta) {
//             const ahora = new Date();
//             const horaActual = ahora.toTimeString().split(' ')[0];
//             const horaLimiteCheckout = guest.SFHotelCheckout || "09:00:00";

//             // Sigue ocupado si todavía no se pasó de las 09:00 AM
//             return horaActual <= horaLimiteCheckout;
//         }
//     }

//     // --- BLOQUE 3: CELDAS DEL FUTURO (Proyecciones) ---
//     if (dateKey > TODAY) {
//         // Reservas o huéspedes actuales proyectados para sus noches correspondientes
//         return code === "I";
//     }

//     return false;
// };
export const occupiesPlazaOnDate = (guest, dateKey) => {
    const code = String(guest.reservaStatusCode || "").trim().toUpperCase();
    if (code === "C" || code === "R") return false;

    const desde = toDateKey(guest.fechaDesde);
    const hasta = toDateKey(guest.fechaHasta);
    const TODAY = moment().format("YYYY-MM-DD");

    if (code === "I") {
    if (dateKey >= desde && dateKey < hasta) return true;
    if (dateKey === hasta) return true; // checkout pendiente, siempre ocupa
    return false;
}

    if (code === "O") {
        // Solo suma en el día que estuvo hospedado (su noche, no el día de salida)
        return dateKey >= desde && dateKey < hasta;
    }

    return false;
};

export const isReservaVisibleEnFecha = (guest, dateKey) => {
    const code = String(guest.reservaStatusCode || "").trim().toUpperCase();
    if (code === "C") return false;

    const desde = toDateKey(guest.fechaDesde);
    const hasta = toDateKey(guest.fechaHasta);
    const TODAY = moment().format("YYYY-MM-DD");

    // Check-in activo: visible en todas sus noches Y en el día hasta (checkout pendiente)
    if (code === "I") {
        return dateKey >= desde && dateKey <= hasta;
    }

    // Checkout realizado o Reservada: solo visible en sus noches (desde <= dateKey < hasta)
    if (code === "O" || code === "R") {
        return dateKey >= desde && dateKey < hasta;
    }

    return false;
};

/**
 * Indica si el huésped hace check-out en la fecha dada (día de salida).
 * No aplica a cancelados.
 */
export const isCheckoutDate = (guest, dateKey) => {
    const code = String(guest.reservaStatusCode || "").trim().toUpperCase();
    if (code === "C") return false;
    const hasta = toDateKey(guest.fechaHasta);
    return hasta && hasta === dateKey;
};

/**
 * Obtiene huéspedes que hacen check-out ese día.
 */
export const getCheckoutGuests = (guests, dateKey) =>
    (guests ?? []).filter((g) => isCheckoutDate(g, dateKey));

/** Huéspedes que ocupan plaza en un día (sin cancelados ni checkout ese día) */
export const countOccupiedPlazas = (guests, dateKey) =>
    (guests ?? []).filter((g) => occupiesPlazaOnDate(g, dateKey)).length;

export const getOccupancyStats = (guests, plazas, dateKey) => {
    const ocupados = countOccupiedPlazas(guests, dateKey);
    const totalPlazas = Number(plazas);
    const hasPlazas = Number.isFinite(totalPlazas) && totalPlazas >= 0;
    const libres = hasPlazas ? Math.max(0, totalPlazas - ocupados) : null;
    return { ocupados, libres, plazas: hasPlazas ? totalPlazas : null };
};

export const countGuestsByStatus = (guests, dateKey) => {
    const counts = { R: 0, I: 0, O: 0, C: 0 };
    (guests ?? []).forEach((guest) => {
        const code = String(guest.reservaStatusCode || "R").trim().toUpperCase();
    
        if (Object.prototype.hasOwnProperty.call(counts, code)) {
            counts[code] += 1;
        } else {
            counts.R += 1;
        }
    });
    
    return STATUS_ORDER.map((code) => ({ code, count: counts[code] })).filter(
        ({ count }) => count > 0
    );
};

/**
 * Carga huéspedes/reservas para cada día de la semana en paralelo.
 * @returns {Record<string, Record<string, object[]>>} roomId -> dateKey -> guests[]
 */
export const fetchWeekOccupancy = async (credentials, SFHotelUsuarioGet, hotelId, weekStart) => {
    const days = getWeekDays(weekStart);
    const results = await Promise.all(
        days.map((day) =>
            SFHotelUsuarioGet(credentials, {
                SFHotelId: Number(hotelId),
                fecha: day.format("YYYY-MM-DD"),
                reservaStatus: null,
            })
        )
    );


    const matrix = {};
    const historico = {}; // roomId -> historicoStr

    days.forEach((day, dayIndex) => {
        const dateKey = day.format("YYYY-MM-DD");
        
        // Extract historico directly from the raw results to avoid losing it if there are no guests
        const rawResults = results[dayIndex] || [];
        rawResults.forEach((rawItem) => {
            if (rawItem.SFHabitacionId && rawItem.SFHabitacionHistoricoEstados) {
                const roomId = String(rawItem.SFHabitacionId);
                if (!historico[roomId]) {
                    historico[roomId] = rawItem.SFHabitacionHistoricoEstados;
                }
            }
        });

        const guests = mapHotelUsuarioList(rawResults);
        guests.forEach((guest) => {
            // Excluir reservas canceladas
            const code = String(guest.reservaStatusCode || "").trim().toUpperCase();
            if (code === "C") return;

            const roomId = String(guest.SFHabitacionId);
            if (!matrix[roomId]) matrix[roomId] = {};
            if (!matrix[roomId][dateKey]) matrix[roomId][dateKey] = [];
            const exists = matrix[roomId][dateKey].some(
                (g) => g.SFHabitacionHuespedId === guest.SFHabitacionHuespedId
            );
            if (!exists) matrix[roomId][dateKey].push(guest);
        });
    });
    return { matrix, days, historico };
};
